import { useMemo } from 'react';
import { useLocation } from 'react-router';

import type { BreadcrumbLink } from '#/components/Breadcrumbs/Breadcrumbs';
import { Breadcrumbs } from '#/components/Breadcrumbs/Breadcrumbs';
import { Byline } from '#/components/Byline/Byline';
import { H1 } from '#/components/Text/H1';
import { useIsClient } from '#/hooks/useIsClient';
import type { CmsApiDocument } from '#/modules/cms/api/types/CmsApiDocument';
import { CmsCategories } from '#/modules/cms/components/Categories/CmsCategories';
import type { CmsPostLocationState } from '#/modules/cms/documents/Post/CmsPostLocationState';
import { getIsContentFullWidthLayout } from '#/modules/cms/helpers/getIsContentFullWidthLayout';

import {
  CmsPostBodyHeaderBreadcrumbsHolder,
  CmsPostBodyHeaderCategoriesHolder,
} from './CmsPostBodyHeader.styled';
import { getCmsPostPathwayLinks } from './getCmsPostPathwayLinks';

type Props = {
  content: CmsApiDocument;
};

export function CmsPostBodyHeader({ content }: Props) {
  const fullWidthLayout = getIsContentFullWidthLayout({ content });

  const isClient = useIsClient();
  const location = useLocation<CmsPostLocationState>();
  const subsite = isClient
    ? (location.state?.subsite ?? content.subsite)
    : content.subsite;

  const links: BreadcrumbLink[] = useMemo(
    () => [
      {
        title: subsite.publicName,
        to: `/${subsite.homePage?.url}`,
        qaId: 'breadcrumbs-home-link',
      },
      {
        title: content.title,
        to: `/${content.url}`,
        state: {
          subsite,
        },
      },
    ],

    [subsite, content],
  );

  return (
    <>
      {content.type === 'POST' && subsite.homePage ? (
        <CmsPostBodyHeaderBreadcrumbsHolder>
          <Breadcrumbs
            links={links}
            mobileLinks={getCmsPostPathwayLinks(content)}
          />
        </CmsPostBodyHeaderBreadcrumbsHolder>
      ) : undefined}

      {content.type === 'POST' &&
        content.categories &&
        content.categories.length > 0 && (
          <CmsPostBodyHeaderCategoriesHolder>
            <CmsCategories
              categories={content.categories}
              homePageUrl={content.subsite.homePage?.url}
            />
          </CmsPostBodyHeaderCategoriesHolder>
        )}

      <H1
        style={{ margin: '0', textAlign: fullWidthLayout ? 'center' : 'left' }}
      >
        {content.title}
      </H1>

      {(content.author ?? (content.postedDate && content.showDate)) && (
        <div style={{ marginTop: 32 }}>
          <Byline
            slug={content.author ? content.author.slug : undefined}
            name={content.author ? content.author.name : undefined}
            image={content.author?.image ?? undefined}
            homePageUrl={content.subsite.homePage?.url}
            postedDate={content.postedDate}
            showDate={content.showDate}
          />
        </div>
      )}
    </>
  );
}
